// ---------------------------------------------------------------------------
// Copy for the business-area landing pages (hub, MS Trader, JAMA).
// Kept apart from english.ts / german.ts on purpose: those two files carry
// the product texts and the legal wording and are edited by hand.
//
// House rule for MS Trader: describe the product, never the current customer
// project — no exchange names, venue codes, customer infrastructure or
// release numbers of a specific trading platform.
// ---------------------------------------------------------------------------
import { Lang } from "config/i18n/languages";
import frCopy from "./locales/fr/landing.json";
import esCopy from "./locales/es/landing.json";
import itCopy from "./locales/it/landing.json";
import plCopy from "./locales/pl/landing.json";
import zhCopy from "./locales/zh/landing.json";
import jaCopy from "./locales/ja/landing.json";
import koCopy from "./locales/ko/landing.json";

export interface Stripe {
  name: string;
  kicker: string;
  text: string;
  cta: string;
}

export interface Area extends Stripe {
  points: string[];
}

export interface Card {
  title: string;
  text: string;
}

export interface Stat {
  value: string;
  label: string;
}

export interface FixGroup {
  title: string;
  chips: string[];
}

export interface Reference {
  sport: string;
  title: string;
  text: string;
  link: string;
  url: string;
  appLink?: string;
  appUrl?: string;
}

const de = {
  nav: {
    home: "Überblick",
    marksim: "Marktsimulation",
    mstrader: "MS Trader",
    jama: "JAMA",
    architecture: "Architektur & FIX",
    hiring: "We are hiring",
  },
  common: {
    contact: "Kontakt aufnehmen",
    more: "Mehr erfahren",
    back: "Zurück zur Produktseite",
  },

  home: {
    eyebrow: "MARKSIM Services GmbH · Wien",
    stripes: [
      {
        name: "MARKSIM",
        kicker: "Marktsimulation",
        text: "Conjoint-Analyse in Eigenregie, Marktsegmentierung und Marktanteilsprognosen – als SaaS, mit Ergebnissen in Minuten statt Tagen.",
        cta: "Zur Marktsimulation",
      },
      {
        name: "MS Trader",
        kicker: "FIX Trading Front End",
        text: "Browserbasiertes Handelsfrontend auf Basis von FIX 4.4: Handel an mehreren Börsen sowie Orders, Bulk-Quotes und OTC-Meldungen in einer einheitlichen Oberfläche.",
        cta: "Zum MS Trader",
      },
      {
        name: "JAMA",
        kicker: "Verbandsmanagement",
        text: "Websites, Mitgliederverwaltung, Turniere, Ticketing, Webshop und App für Sportverbände und Vereine – im Einsatz im Judo und im Bogensport.",
        cta: "Zu JAMA",
      },
    ] as Stripe[],
    intro: {
      title: ["Drei Lösungen. Ein Ziel:", "HA – High Availability"],
      text: "Unser Anspruch verbindet alle drei Lösungen: Hohe Verfügbarkeit komplexer Prozesse auf zuverlässiger Technik. Damit Sie sich auf das konzentrieren können, was Sie voranbringt.",
      tagline: "Entwickelt in Wien. Für eine globale Kundenzielgruppe.",
    },
    areas: [
      {
        name: "MARKSIM",
        kicker: "Marktsimulation",
        text: "Bauen Sie mit Hilfe von KI Ihre eigenen Marktmodelle und erstellen Sie belastbare Prognosen für Ihre Businesspläne.",
        points: [
          "Marktposition und tatsächliche Wettbewerber transparent machen",
          "Kaufmotive verstehen und Produkte gezielt optimieren",
          "Künftige Marktanteile belastbar prognostizieren",
        ],
        cta: "Zur Marktsimulation",
      },
      {
        name: "MS Trader",
        kicker: "FIX Trading Front End",
        text: "Handelsfrontend für mehrere Börsen: je Trader ein eigenes Gateway mit eigener FIX-Session.",
        points: [
          "Orders, Bulk-Quotes, Orderbuch, Blotter und News in einer einheitlichen Oberfläche",
          "OTC- und Blocktrade-Meldungen direkt aus dem Frontend",
          "Beliebig viele Trader dank horizontaler Skalierung",
          "FIX-Proxy zur Entlastung der Börseninfrastruktur",
          "FIX-Marktdaten-Collector mit Unterstützung für PostgreSQL/TimescaleDB",
          "Test-Suite für umfassende Tests mit Graylog-Anbindung und PDF-Berichten",
        ],
        cta: "Zum MS Trader",
      },
      {
        name: "JAMA",
        kicker: "Verbandsmanagement",
        text: "Die flexible Gesamtlösung für Sportverbände und Vereine – Website, Verwaltung, Turniere, Ticketing, Webshop und App.",
        points: [
          "Rollenbasierte Verwaltung für Verbände und Vereine",
          "CMS mit eigener Vereinswebsite unter einer Subdomain",
          "Mitgliedskarten-App für iOS und Android",
          "Webshop mit Zahlungsabwicklung über Stripe",
        ],
        cta: "Zu JAMA",
      },
    ] as Area[],
    cta: {
      title: "Wir freuen uns auf Ihr Projekt.",
      text: "Ob Marktforschung, Börsenanbindung oder Verbandsverwaltung – wir beraten Sie persönlich und stellen Ihnen die passende Lösung vor.",
      button: "Kontakt aufnehmen",
    },
  },

  mstrader: {
    pageTitle: "MS Trader – FIX Trading Front End",
    eyebrow: "MS Trader · FIX-4.4-Handelsfrontend",
    title: ["Mehrere Börsen verwalten und handeln.", "Direkt aus dem Browser."],
    lead: "MS Trader ist ein FIX-4.4-Trading-Frontend für mehrere Börsen und Marktplätze. Jeder Trader erhält ein eigenes Gateway mit eigener FIX-Session zur Börse; Orders, Bulk-Quotes für Market Maker sowie OTC- und Blocktrade-Meldungen laufen in einer Oberfläche zusammen. Installiert auf Ihrer Hardware – auch in Netzen ohne Internetzugang.",
    primary: "Architektur & FIX-Fähigkeiten",
    secondary: "Demo anfragen",
    stats: [
      { value: "Multi", label: "Mehrere Börsen in einer Oberfläche verwalten und handeln" },
      { value: "FIX 4.4", label: "Handel und Marktdaten über das Standardprotokoll der Börsen" },
      { value: "1 : 1", label: "Ein Gateway und eine FIX-Session pro Trader" },
      { value: "∞", label: "Unbegrenzte Anzahl an Tradern durch horizontale Skalierung" },
    ] as Stat[],
    showcase: {
      title: ["Alles,", "was ein Trader braucht."],
      items: [
        {
          title: "Chart & Indikatoren",
          text: "Kerzen- oder Linienchart je Instrument mit Indikatoren wie gleitenden Durchschnitten und Bollinger-Bändern, Fibonacci-Retracements, Zeichenwerkzeugen und Volumen. Auktionen, Trade-at-Close und Schlusskurse sind im Chart markiert; mehrere Instrumente lassen sich in Prozent vergleichen, von Intraday bis zur Gesamthistorie.",
        },
        {
          title: "Marktfenster & Watchlist",
          text: "Echtzeitkurse, Handelsphase und Status je Instrument. Abonnement nach ISIN oder ganzen Marktsegmenten, mehrere Fenster mit Window-Linking, Indizes und meistgehandelte Werte auf einen Blick.",
        },
        {
          title: "Working Orders & Trade Blotter",
          text: "Alle offenen Orders mit Status, Fills und Ablehnungsgrund. Ändern und Stornieren mit einem Klick; Ausführungen, Eigengeschäfte und öffentliche Trades im Blotter mit Export.",
        },
        {
          title: "OTC-, Blocktrades & Bulk Quotes",
          text: "OTC- und Blocktrade-Meldungen mit Owner- und Counterparty-Angaben direkt aus der Oberfläche. Market Maker pflegen Einzel- und Massenquotes und laden Bulk-Quotes aus einer Tabelle – Status und Ablehnungen je Quote im Blick.",
        },
      ] as Card[],
    },
    more: {
      title: ["Und", "darüber hinaus."],
      items: [
        {
          title: "Mehrere Börsen",
          text: "Marktdaten und Handel für mehrere Börsen und Marktplätze in einer Oberfläche – je Börse eigene Konnektoren, ein Marktwähler für den Trader.",
        },
        {
          title: "News & Börsenmeldungen",
          text: "Technische und Markt-News in Echtzeit, Sicherheitsstatus und Handelsphasen je Instrument.",
        },
        {
          title: "Indizes & Segmente",
          text: "Indexlisten und Marktsegmente je Börse als eigene Fenster; Instrumente direkt daraus abonnieren, abgelaufene Werte auf Wunsch ausblenden.",
        },
        {
          title: "Horizontale Skalierung",
          text: "Ein Gateway-Pod je Trader, dynamisch beim Login gestartet: Die Anzahl der Trader ist nicht begrenzt, sondern wächst mit dem Cluster.",
        },
        {
          title: "Handelskalender",
          text: "Feiertage und nächste Handelstage je Börse, angezeigt im Session-Fenster – die Börse bleibt die Autorität.",
        },
        {
          title: "Desktops & Layouts",
          text: "Mehrere Desktops, andockbare Fenster und gespeicherte Layouts pro Trader.",
        },
        {
          title: "Orderbuch (Depth of Market)",
          text: "Bid- und Ask-Tiefe live aus dem Snapshot der Börse. Ein Klick auf eine Zeile befüllt die Order-Maske vor; Tick-Größen werden bereits bei der Preiseingabe validiert.",
        },
        {
          title: "Order-Ticket",
          text: "Limit, Market, Stop, Trailing Stop, Iceberg und OCO. MiFID-II-Party-Identifikation, Self-Match-Prevention, Direct Electronic Access und Auction Volume Discovery direkt im Formular – kompakt oder erweitert.",
        },
      ] as Card[],
    },
    orderTypes: {
      title: "Ordertypen und Gültigkeiten",
      chips: [
        "Limit",
        "Market",
        "Stop",
        "Trailing Stop (absolut / %)",
        "Iceberg",
        "OCO",
        "Trade-at-Close",
        "DAY · GTC · GTD · IOC · FOK",
        "Dezimale Stückzahlen",
        "Preis-, Wert- und Volumenprüfung",
      ],
    },
    ops: {
      title: ["Gebaut für", "regulierte Umgebungen."],
      items: [
        {
          title: "Signierte Auslieferung",
          text: "Container-Images werden mit cosign signiert und aus einer privaten Registry bezogen; die Signatur wird bei jedem Pull geprüft.",
        },
        {
          title: "Offline-Installation",
          text: "Deploy-Bundle für Kubernetes auf Ihrer Hardware – auch in Netzen ohne Internetzugang.",
        },
        {
          title: "Starke Authentifizierung",
          text: "argon2-Passwort-Hashes, TOTP als zweiter Faktor und kurzlebige, asymmetrisch signierte Tokens (ES256).",
        },
        {
          title: "Isolation je Trader",
          text: "Ein Gateway-Pod, eine FIX-Session und eigene Berechtigungen pro Trader; Marktdaten-Umfang und -Tiefe werden serverseitig durchgesetzt.",
        },
        {
          title: "Hochverfügbare Daten",
          text: "PostgreSQL mit TimescaleDB, Patroni-Cluster über mehrere Standorte und Backups mit Barman.",
        },
        {
          title: "Admin-Konsole",
          text: "Systemüberblick, Trader-Verwaltung, FIX-Identitäten, Marktaufsicht und Audit-Trail.",
        },
        {
          title: "Mandantenfähig",
          text: "Isolierte Umgebungen mit eigenen Datenbanken, Namespace und Hostname per Kommando anlegen, deaktivieren und löschen.",
        },
        {
          title: "Simulation & Produktion",
          text: "Getrennte Umgebungen gegen die Börsen-Simulation und gegen echte Produktionsmarktdaten – testen ohne Risiko.",
        },
      ] as Card[],
    },
    cta: {
      title: "MS Trader in Ihrem Haus?",
      text: "Wir zeigen Ihnen MS Trader in der Simulationsumgebung und besprechen die Integration in Ihre Infrastruktur.",
      button: "Kontakt aufnehmen",
      secondary: "Architektur ansehen",
    },
  },

  architecture: {
    pageTitle: "MS Trader – Architektur & FIX",
    eyebrow: "MS Trader · Architektur",
    title: ["Architektur &", "FIX-Fähigkeiten"],
    lead: "Wie MS Trader aufgebaut ist: welche Services es gibt, wem welche FIX-Session gehört, welche Datenbank was besitzt – und was das System an FIX 4.4 beherrscht.",
    services: {
      title: ["Die", "Services"],
      head: ["Service", "Aufgabe", "Läuft als"],
      rows: [
        ["gateway", "Ein Prozess pro Trader. Hält dessen FIX-Handelssession, bedient die REST-API und den WebSocket der Oberfläche.", "Kubernetes-Pod, beim Login gestartet, bei Inaktivität beendet"],
        ["mslogin", "Authentifiziert den Trader, startet und beendet dessen Gateway-Pod, stellt das Token aus.", "Deployment"],
        ["fixproxy", "Eine FIX-Marktdatensession zur Börse, per FIX an alle Gateways verteilt. Eine Instanz je Börse.", "StatefulSet"],
        ["mdcollector", "Zweite, unabhängige FIX-Session zur Börse. Schreibt Ticks, Trades, Sicherheitsstatus und News direkt in TimescaleDB. Eine Instanz je Börse.", "StatefulSet"],
        ["ms-trader-ui", "Das Trader-Frontend im Browser.", "Deployment"],
        ["admin", "Admin-Konsole: Trader, Berechtigungen, FIX-Identitäten, Marktaufsicht, Handelskalender.", "Deployment (optional)"],
        ["authd", "Datenbankloser Login am Netzwerkrand: Hash-Liste und TOTP.", "Deployment (optional)"],
        ["pgdog", "Connection-Pooler vor PostgreSQL.", "Deployment (optional)"],
      ],
    },
    flow: {
      title: ["Datenfluss", "auf einen Blick"],
      caption: "Marktdaten kommen zweimal von der Börse: fixproxy verteilt sie live an alle Gateways, mdcollector schreibt sie in die Historie. Orders gehen ausschließlich über die Handelssession des jeweiligen Gateways. Weitere Börsen bedeuten weitere Konnektor-Paare, weitere Trader weitere Gateway-Pods.",
    },
    sessions: {
      title: ["Vier Arten von", "FIX-Sessions"],
      head: ["#", "Von → Nach", "Rolle", "Persistenz"],
      rows: [
        ["1", "mdcollector → Börse", "Initiator, Marktdaten", "FileStore oder SQL"],
        ["2", "fixproxy → Börse", "Initiator, Marktdaten", "FileStore oder SQL"],
        ["3", "gateway → Börse", "Initiator, Handel – die einzige Session, die Orders platziert", "SQL: Sequenznummern und Resend-Store in PostgreSQL"],
        ["4", "gateway → fixproxy", "Initiator, Marktdaten innerhalb des Clusters", "Memory-Store, Reset bei jedem Verbindungsaufbau"],
      ],
      note: "Alle Sessions sind FIX 4.4 über TLS-gesichertes TCP; die Verbindung zur Börse läuft durch einen VPN-Tunnel. Die Identität einer Session (CompIDs, Zielhost) kommt aus der Datenbank, nicht aus dem Image.",
    },
    databases: {
      title: ["Zwei Datenbanken,", "klar getrennt"],
      items: [
        {
          title: "mstrader – private Daten",
          text: "Trader, FIX-Identitäten, Orders, Ausführungen, Positionen, Einstellungen sowie Sequenznummern und gesendete Nachrichten der Handelssessions.",
        },
        {
          title: "msmarketdata – öffentliche Daten",
          text: "Wertpapierkatalog, Ticks, Trades, Sicherheitsstatus, Tick-Größen und News in TimescaleDB – geschrieben ausschließlich von mdcollector, von allen anderen nur gelesen.",
        },
      ] as Card[],
    },
    fix: {
      title: ["FIX 4.4", "im Detail"],
      lead: "Die Nachrichtentypen, die MS Trader gegenüber der Börse spricht – FIX 4.4 mit den Erweiterungen der jeweiligen Handelsplattform.",
      groups: [
        {
          title: "Handel",
          chips: ["NewOrderSingle (D)", "OrderCancelRequest (F)", "OrderCancelReplaceRequest (G)", "OrderStatusRequest (H)", "OrderMassStatusRequest (AF)", "ExecutionReport (8)", "OrderCancelReject (9)"],
        },
        {
          title: "Quotes & Market Making",
          chips: ["Quote (S)", "MassQuote (i)", "QuoteRequest (R)", "Bulk-Quotes aus Tabellen"],
        },
        {
          title: "Marktdaten",
          chips: ["MarketDataRequest (V)", "SnapshotFullRefresh (W)", "IncrementalRefresh (X)", "SecurityList (x / y)", "TickSizeList (XTL)", "SecurityStatus (e / f)", "TradingSessionStatus (g)"],
        },
        {
          title: "Trades, OTC & Meldungen",
          chips: ["TradeCaptureReport (AD / AE) inkl. Tagesreplay", "OTC- / Blocktrade-Reports", "News (B)", "BusinessMessageReject (j)"],
        },
        {
          title: "Regulatorik im Order-Ticket",
          chips: ["MiFID-II-Party-Identifikation (Tag 453)", "Liquidity-Provision-Flag (Tag 2593)", "Direct Electronic Access", "Self-Match-Prevention", "Auction Volume Discovery", "Preis-, Wert- und Volumenprüfung"],
        },
      ] as FixGroup[],
      dictionary: {
        title: "Datenwörterbuch",
        text: "Das Basis-Dictionary FIX 4.4 wird mit den Erweiterungen der Börse zusammengeführt; aus dem Ergebnis wird der Code für alle Nachrichtentypen generiert. Neue Spezifikationsversionen sind damit ein Werkzeuglauf, keine Handarbeit.",
      } as Card,
    },
    resilience: {
      title: ["Robust", "im Tagesbetrieb"],
      items: [
        {
          title: "Markt-verbunden-Gate",
          text: "Eine eingeloggte Session ist noch kein offener Markt. Subskriptionen und neue Orders warten, bis die Börse den Markt als verbunden meldet; Stornos und Änderungen gehen immer durch.",
        },
        {
          title: "Sequenznummern & Recovery",
          text: "Sequenznummern werden persistiert; ein Prozessabsturz am Handelstag wird per Resend-Request und Gap-Fill geheilt. Gateways nutzen dafür PostgreSQL und starten deshalb auf jedem Knoten neu.",
        },
        {
          title: "Fan-out mit Koaleszierung",
          text: "Je Gateway-Session eine begrenzte Sendewarteschlange; bei Stau bleibt nur der neueste Snapshot je ISIN. Ein langsamer Client kann den Börsenkanal nicht blockieren.",
        },
        {
          title: "Adaptive Drosselung",
          text: "Meldet die Börse eine überschrittene Nachrichtenrate, verdoppelt fixproxy den Sendeabstand und normalisiert nach fehlerfreiem Lauf wieder.",
        },
        {
          title: "Cache & Replay",
          text: "fixproxy hält je ISIN den letzten Stand und spielt ihn bei Subskription oder Reconnect sofort ein – inklusive Handelsphase nach Börsenschluss.",
        },
        {
          title: "Durchsatz",
          text: "Ticks, Trades und Statusmeldungen gehen per Bulk-Copy in TimescaleDB: 50.000 bis 150.000 Zeilen pro Sekunde je Collector.",
        },
      ] as Card[],
    },
    security: {
      title: ["Sicherheit &", "Betrieb"],
      items: [
        "Signierte Container-Images (cosign) aus einer privaten Registry, Prüfung bei jedem Pull",
        "argon2-Passwörter, TOTP und kurzlebige ES256-Tokens; Einmal-Tickets für WebSockets",
        "Berechtigungen je Trader für Handel, Quotes und Marktdaten – serverseitig im Gateway erzwungen",
        "Kubernetes mit Traefik und Longhorn; Deploy-Bundle für Offline-Installationen",
        "PostgreSQL 18 mit TimescaleDB, Patroni-Cluster über mehrere Standorte, Backups mit Barman",
        "Mandanten per Kommando: eigene Datenbanken, Namespace und Hostname",
        "Getrennte Simulations- und Produktionsumgebungen mit Health-Endpunkten und Metriken",
      ],
    },
    cta: {
      title: "Fragen zur Integration?",
      text: "Wir gehen Ihre Infrastruktur, Ihre FIX-Identitäten und Ihre Anforderungen an Hochverfügbarkeit gemeinsam durch.",
      button: "Kontakt aufnehmen",
    },
  },

  jama: {
    pageTitle: "JAMA – Verbandsmanagement",
    eyebrow: "JAMA · Verbandsmanagement",
    title: ["Ihr Partner für", "modernes Verbandsmanagement."],
    lead: "JAMA ist die flexible Lösung für Sportverbände und Vereine: Websites, Mitgliederverwaltung, Einladungs-Management, Turnierverwaltung, Ticketsystem, digitale Mitgliedskarte (App), Webshop und vieles mehr – alles aus einer Hand, maßgeschneidert für Ihre Bedürfnisse. Im Einsatz im Judo und im Bogensport.",
    primary: "judojama.com besuchen",
    secondary: "Kontakt aufnehmen",
    url: "https://www.judojama.com/",
    components: {
      title: ["Unsere", "Komponenten"],
      items: [
        {
          title: "Verwaltung",
          text: "Ein umfangreiches Verwaltungsinterface für Bundes- und Landesverbände sowie Vereine. Vollständig rollenbasiert: Je nach Funktion – Generalsekretariat, Verbandsadministration, Trainer, Kampfrichter, Obleute, Schriftführung – erhält eine Person mehr oder weniger Rechte. Von der Turnieranmeldung via App bis zur Startgeldabrechnung.",
        },
        {
          title: "Websites / CMS",
          text: "Ein Content-Management-System für den Bundesverband und für alle Vereine. Kalender, Vorstand und Funktionäre, Kader und News werden automatisch eingebunden; jeder Verein bekommt seine eigene Homepage als Subdomain.",
        },
        {
          title: "Event-Management",
          text: "Wettkämpfe, Trainings, Veranstaltungen und Prüfungen anlegen und automatisch im Kalender auf der Website und in der App anzeigen. Verbands-Events sind für alle sichtbar, Vereins-Events auf der jeweiligen Vereinsseite.",
        },
        {
          title: "Lizenzverwaltung",
          text: "Jährlicher Mitgliedsbeitrag, Trainerlizenz oder historische Lizenzen: alle Möglichkeiten der umfassenden Lizenzverwaltung mit automatischer Liste derjenigen, deren Lizenz abzulaufen droht.",
        },
        {
          title: "Ligasystem",
          text: "Umfangreiches Bundes- und Landesligasystem mit Ergebnissen, Tabellen und Wertungen. Ergebnisse können über eine offene API in jedes andere System oder jede App integriert werden.",
        },
        {
          title: "Qualifizierungspfade",
          text: "Hierarchien von Qualifikationen hinterlegen und für jeden Sportler automatisch einen Qualifizierungspfad erstellen. Nach bestandener Prüfung – etwa einer KYU-Prüfung im Judo – wird der neue Grad automatisch eingetragen und ist sofort in der App sichtbar.",
        },
        {
          title: "Ticketsystem",
          text: "Keine teuren externen Ticket-Systeme: Eintrittskarten direkt in JAMA erstellen und online verkaufen. Die Tickets sind direkt in der Mitgliedskarten-App sichtbar.",
        },
        {
          title: "Mitgliedskarten-App",
          text: "Der aktuelle Sportpass auf dem Smartphone – für iPhone und Android. News, Turnier-Kalender, Mitglieds- und Lizenzkarten; Eltern verwalten die Pässe ihrer Kinder. Mit eigenem Trainer- und Obmann-Modus.",
        },
        {
          title: "Webshop mit Stripe",
          text: "Merchandise, Startgelder, Lizenzen oder Tickets online verkaufen: Der Webshop lässt sich mit Zahlungsabwicklung über Stripe umsetzen – Bestellungen und Zahlungen laufen direkt in der Verwaltung zusammen.",
        },
        {
          title: "Kommunikation",
          text: "Mitglieder in Echtzeit über neue Turniere, Terminverschiebungen und Maßnahmen informieren – direkt und just in time.",
        },
      ] as Card[],
    },
    references: {
      title: ["JAMA", "im Einsatz"],
      items: [
        {
          sport: "Judo",
          title: "JudoJama",
          text: "Verbands- und Vereinsverwaltung für den Judosport – mit der App „JudoAustria“ im Google Play Store: Turnierkalender, News und digitale Mitgliedskarte.",
          link: "judojama.com",
          url: "https://www.judojama.com/",
          appLink: "JudoAustria bei Google Play",
          appUrl: "https://play.google.com/store/apps/details?id=org.judojama.judoaustria",
        },
      ] as Reference[],
    },
    cta: {
      title: "Alles aus einer Hand.",
      text: "Website, Verwaltung, App, Ticketsystem und Webshop – maßgeschneidert für Ihren Verband oder Verein, unabhängig von der Sportart. Wir zeigen Ihnen JAMA gerne live.",
      button: "Kontakt aufnehmen",
      secondary: "judojama.com",
    },
  },
};

export type LandingCopy = typeof de;

const en: LandingCopy = {
  nav: {
    home: "Overview",
    marksim: "Market Simulation",
    mstrader: "MS Trader",
    jama: "JAMA",
    architecture: "Architecture & FIX",
    hiring: "We are hiring",
  },
  common: {
    contact: "Get in touch",
    more: "Learn more",
    back: "Back to the product page",
  },

  home: {
    eyebrow: "MARKSIM Services GmbH · Vienna",
    stripes: [
      {
        name: "MARKSIM",
        kicker: "Market Simulation",
        text: "Do-it-yourself conjoint analysis, market segmentation and market share prediction – as SaaS, in minutes instead of days.",
        cta: "Explore Market Simulation",
      },
      {
        name: "MS Trader",
        kicker: "FIX Trading Front End",
        text: "Browser-based trading front end with FIX 4.4: manage and trade on multiple exchanges – orders, bulk quotes and OTC reports in one UI.",
        cta: "Explore MS Trader",
      },
      {
        name: "JAMA",
        kicker: "Federation Management",
        text: "Websites, members, tournaments, tickets, web shop and app for sports federations and clubs – in use in judo and archery.",
        cta: "Explore JAMA",
      },
    ],
    intro: {
      title: ["Three solutions. One goal:", "HA – High Availability"],
      text: "One standard runs through all three of our solutions: high availability for complex processes, built on reliable technology. So you can focus on what drives you forward.",
      tagline: "Developed in Vienna. For customers worldwide.",
    },
    areas: [
      {
        name: "MARKSIM",
        kicker: "Market Simulation",
        text: "Build your own market models with the help of AI and create robust forecasts for your business plans.",
        points: [
          "See your market position and your real competitors",
          "Understand buyer motives and optimise products",
          "Predict tomorrow's market shares with confidence",
        ],
        cta: "Explore Market Simulation",
      },
      {
        name: "MS Trader",
        kicker: "FIX Trading Front End",
        text: "A trading front end for multiple exchanges: one gateway and one FIX session per trader.",
        points: [
          "Orders, bulk quotes, order book, blotters and news in one UI",
          "OTC and block trade reports straight from the front end",
          "Unlimited number of traders through horizontal scaling",
          "FIX proxy that reduces the load on exchange infrastructure",
          "FIX market-data collector with PostgreSQL/TimescaleDB support",
          "Test suite for comprehensive testing, with Graylog support and PDF reports",
        ],
        cta: "Explore MS Trader",
      },
      {
        name: "JAMA",
        kicker: "Federation Management",
        text: "The flexible solution for sports federations and clubs – website, administration, tournaments, tickets, web shop and app.",
        points: [
          "Role-based administration for federation and club",
          "CMS with a club homepage on its own subdomain",
          "Membership card app for iPhone and Android",
          "Web shop with payments through Stripe",
        ],
        cta: "Explore JAMA",
      },
    ],
    cta: {
      title: "Let's talk about your project.",
      text: "Market research, exchange connectivity or federation administration – we advise you personally and show you the solution that fits.",
      button: "Get in touch",
    },
  },

  mstrader: {
    pageTitle: "MS Trader – FIX Trading Front End",
    eyebrow: "MS Trader · FIX 4.4 trading front end",
    title: ["Manage and trade on multiple exchanges.", "Straight from the browser."],
    lead: "MS Trader is a FIX 4.4 trading front end for multiple exchanges and venues. Every trader gets their own gateway with their own FIX session to the exchange; orders, bulk quotes for market makers and OTC and block trade reports come together in one UI. Installed on your hardware – including sites with no internet access.",
    primary: "Architecture & FIX capabilities",
    secondary: "Request a demo",
    stats: [
      { value: "Multi", label: "Manage and trade on multiple exchanges in one UI" },
      { value: "FIX 4.4", label: "Trading and market data over the exchanges' standard protocol" },
      { value: "1 : 1", label: "One gateway and one FIX session per trader" },
      { value: "∞", label: "Unlimited number of traders through horizontal scaling" },
    ],
    showcase: {
      title: ["Everything", "a trader needs."],
      items: [
        {
          title: "Chart & indicators",
          text: "Candle or line chart per instrument with indicators such as moving averages and Bollinger Bands, Fibonacci retracements, drawing tools and volume. Auctions, trade-at-close and closing prices are marked in the chart; several instruments can be compared in percent, from intraday to the full history.",
        },
        {
          title: "Market window & watchlist",
          text: "Real-time quotes, trading phase and status per instrument. Subscribe by ISIN or whole market segments, link several windows, keep indices and most-traded instruments in view.",
        },
        {
          title: "Working orders & trade blotter",
          text: "All open orders with status, fills and rejection reason. Modify and cancel with one click; executions, own trades and public trades in the blotter, with export.",
        },
        {
          title: "OTC, block trades & bulk quotes",
          text: "OTC and block trade reports with owner and counterparty details straight from the UI. Market makers maintain single and mass quotes and load bulk quotes from a table – with status and rejections per quote in view.",
        },
      ],
    },
    more: {
      title: ["And", "beyond."],
      items: [
        {
          title: "Multiple exchanges",
          text: "Market data and trading for several exchanges and venues in one UI – dedicated connectors per exchange, one market selector for the trader.",
        },
        {
          title: "News & exchange messages",
          text: "Technical and market news in real time, security status and trading phases per instrument.",
        },
        {
          title: "Indices & segments",
          text: "Index lists and market segments per exchange as separate windows; subscribe instruments straight from them and hide expired ones on request.",
        },
        {
          title: "Horizontal scaling",
          text: "One gateway pod per trader, spawned dynamically at login: the number of traders is not capped – it grows with the cluster.",
        },
        {
          title: "Trading calendar",
          text: "Holidays and next trading days per exchange, shown in the session window – the exchange stays the authority.",
        },
        {
          title: "Desktops & layouts",
          text: "Several desktops, dockable windows and saved layouts per trader.",
        },
        {
          title: "Order book (depth of market)",
          text: "Live bid and ask depth from the exchange snapshot. Clicking a row pre-fills the order ticket; tick sizes are validated as the price is typed.",
        },
        {
          title: "Order ticket",
          text: "Limit, market, stop, trailing stop, iceberg and OCO. MiFID II party identification, self-match prevention, direct electronic access and auction volume discovery right in the form – compact or extended.",
        },
      ],
    },
    orderTypes: {
      title: "Order types and validity",
      chips: [
        "Limit",
        "Market",
        "Stop",
        "Trailing stop (absolute / %)",
        "Iceberg",
        "OCO",
        "Trade-at-close",
        "DAY · GTC · GTD · IOC · FOK",
        "Decimal quantities",
        "Price, value and volume checks",
      ],
    },
    ops: {
      title: ["Built for", "regulated environments."],
      items: [
        {
          title: "Signed delivery",
          text: "Container images are signed with cosign and pulled from a private registry; the signature is verified on every pull.",
        },
        {
          title: "Air-gapped installation",
          text: "Deploy bundle for Kubernetes on your hardware – including networks without internet access.",
        },
        {
          title: "Strong authentication",
          text: "argon2 password hashes, TOTP as second factor and short-lived, asymmetrically signed tokens (ES256).",
        },
        {
          title: "Isolation per trader",
          text: "One gateway pod, one FIX session and individual permissions per trader; market data scope and depth are enforced server-side.",
        },
        {
          title: "Highly available data",
          text: "PostgreSQL with TimescaleDB, a Patroni cluster across several sites and backups with Barman.",
        },
        {
          title: "Admin console",
          text: "System overview, trader management, FIX identities, market supervision and audit trail.",
        },
        {
          title: "Multi-tenant",
          text: "Create, disable and delete isolated environments with their own databases, namespace and hostname from the command line.",
        },
        {
          title: "Simulation & production",
          text: "Separate environments against the exchange simulation and against real production market data – test without risk.",
        },
      ],
    },
    cta: {
      title: "MS Trader on your premises?",
      text: "We show you MS Trader in the simulation environment and walk through the integration into your infrastructure.",
      button: "Get in touch",
      secondary: "See the architecture",
    },
  },

  architecture: {
    pageTitle: "MS Trader – Architecture & FIX",
    eyebrow: "MS Trader · Architecture",
    title: ["Architecture &", "FIX capabilities"],
    lead: "How MS Trader is put together: which services exist, which FIX session belongs to whom, which database owns what – and what the system speaks in FIX 4.4.",
    services: {
      title: ["The", "services"],
      head: ["Service", "What it does", "Runs as"],
      rows: [
        ["gateway", "One process per trader. Owns that trader's FIX trading session, serves the REST API and the WebSocket the UI subscribes to.", "Kubernetes pod, spawned on login, reaped when idle"],
        ["mslogin", "Authenticates the trader, spawns and reaps the gateway pod, issues the token.", "Deployment"],
        ["fixproxy", "One FIX market-data session to the exchange, fanned out to every gateway over FIX. One per exchange.", "StatefulSet"],
        ["mdcollector", "A second, independent FIX session to the exchange. Writes ticks, trades, security status and news straight into TimescaleDB. One per exchange.", "StatefulSet"],
        ["ms-trader-ui", "The trader front end in the browser.", "Deployment"],
        ["admin", "Admin console: traders, permissions, FIX identities, market supervision, trading calendar.", "Deployment (optional)"],
        ["authd", "DB-less login at the network edge: hash list and TOTP.", "Deployment (optional)"],
        ["pgdog", "Connection pooler in front of PostgreSQL.", "Deployment (optional)"],
      ],
    },
    flow: {
      title: ["Data flow", "at a glance"],
      caption: "Market data arrives twice from the exchange: fixproxy fans it out live to every gateway, mdcollector writes it to history. Orders travel exclusively over the trading session of the trader's own gateway. More exchanges mean more connector pairs, more traders mean more gateway pods.",
    },
    sessions: {
      title: ["Four kinds of", "FIX sessions"],
      head: ["#", "From → To", "Role", "Persistence"],
      rows: [
        ["1", "mdcollector → exchange", "Initiator, market data", "FileStore or SQL"],
        ["2", "fixproxy → exchange", "Initiator, market data", "FileStore or SQL"],
        ["3", "gateway → exchange", "Initiator, trading – the only session that places orders", "SQL: sequence numbers and resend store in PostgreSQL"],
        ["4", "gateway → fixproxy", "Initiator, market data inside the cluster", "Memory store, reset on every connect"],
      ],
      note: "All sessions are FIX 4.4 over TLS-secured TCP; the link to the exchange runs through a VPN tunnel. A session's identity (CompIDs, target host) comes from the database, not from the image.",
    },
    databases: {
      title: ["Two databases,", "cleanly separated"],
      items: [
        {
          title: "mstrader – private data",
          text: "Traders, FIX identities, orders, executions, positions, preferences, plus sequence numbers and sent messages of the trading sessions.",
        },
        {
          title: "msmarketdata – public data",
          text: "Securities catalogue, ticks, trades, security status, tick sizes and news in TimescaleDB – written exclusively by mdcollector, read-only for everyone else.",
        },
      ],
    },
    fix: {
      title: ["FIX 4.4", "in detail"],
      lead: "The message types MS Trader speaks with the exchange – FIX 4.4 with the extensions of the respective trading platform.",
      groups: [
        {
          title: "Trading",
          chips: ["NewOrderSingle (D)", "OrderCancelRequest (F)", "OrderCancelReplaceRequest (G)", "OrderStatusRequest (H)", "OrderMassStatusRequest (AF)", "ExecutionReport (8)", "OrderCancelReject (9)"],
        },
        {
          title: "Quotes & market making",
          chips: ["Quote (S)", "MassQuote (i)", "QuoteRequest (R)", "Bulk quotes from tables"],
        },
        {
          title: "Market data",
          chips: ["MarketDataRequest (V)", "SnapshotFullRefresh (W)", "IncrementalRefresh (X)", "SecurityList (x / y)", "TickSizeList (XTL)", "SecurityStatus (e / f)", "TradingSessionStatus (g)"],
        },
        {
          title: "Trades, OTC & messages",
          chips: ["TradeCaptureReport (AD / AE) incl. full-day replay", "OTC / block trade reports", "News (B)", "BusinessMessageReject (j)"],
        },
        {
          title: "Regulatory fields in the order ticket",
          chips: ["MiFID II party identification (tag 453)", "Liquidity provision flag (tag 2593)", "Direct electronic access", "Self-match prevention", "Auction volume discovery", "Price, value and volume checks"],
        },
      ],
      dictionary: {
        title: "Data dictionary",
        text: "The base FIX 4.4 dictionary is merged with the exchange's extensions, and the code for every message type is generated from the result. A new specification release is a tool run, not manual work.",
      },
    },
    resilience: {
      title: ["Robust", "in daily operation"],
      items: [
        {
          title: "Market-connected gate",
          text: "A logged-on session is not an open market. Subscriptions and new orders wait until the exchange reports the market as connected; cancels and modifies always go through.",
        },
        {
          title: "Sequence numbers & recovery",
          text: "Sequence numbers are persisted; a process crash during the trading day heals via resend request and gap fill. Gateways keep this state in PostgreSQL and can therefore restart on any node.",
        },
        {
          title: "Fan-out with coalescing",
          text: "A bounded send queue per gateway session; under pressure only the newest snapshot per ISIN is kept. A slow client can never stall the exchange link.",
        },
        {
          title: "Adaptive throttle",
          text: "If the exchange reports an exceeded message rate, fixproxy doubles its send interval and returns to normal after a clean run.",
        },
        {
          title: "Cache & replay",
          text: "fixproxy keeps the latest state per ISIN and replays it instantly on subscribe or reconnect – including the trading phase after the close.",
        },
        {
          title: "Throughput",
          text: "Ticks, trades and status messages are bulk-copied into TimescaleDB: 50,000 to 150,000 rows per second per collector.",
        },
      ],
    },
    security: {
      title: ["Security &", "operations"],
      items: [
        "Signed container images (cosign) from a private registry, verified on every pull",
        "argon2 passwords, TOTP and short-lived ES256 tokens; one-time tickets for WebSockets",
        "Per-trader permissions for trading, quotes and market data – enforced server-side in the gateway",
        "Kubernetes with Traefik and Longhorn; deploy bundle for air-gapped installations",
        "PostgreSQL 18 with TimescaleDB, Patroni cluster across several sites, backups with Barman",
        "Tenants from the command line: own databases, namespace and hostname",
        "Separate simulation and production environments with health endpoints and metrics",
      ],
    },
    cta: {
      title: "Questions about integration?",
      text: "We walk through your infrastructure, your FIX identities and your high-availability requirements together.",
      button: "Get in touch",
    },
  },

  jama: {
    pageTitle: "JAMA – Federation Management",
    eyebrow: "JAMA · Federation management",
    title: ["Your partner for", "modern federation management."],
    lead: "JAMA is the flexible solution for sports federations and clubs: websites, member administration, invitation management, tournament administration, ticketing, a digital membership card (app), web shop and much more – all from a single source, tailored to your needs. In use in judo and archery.",
    primary: "Visit judojama.com",
    secondary: "Get in touch",
    url: "https://www.judojama.com/",
    components: {
      title: ["Our", "components"],
      items: [
        {
          title: "Administration",
          text: "A comprehensive administration interface for national and regional federations as well as clubs. Fully role-based: depending on a person's function – general secretary, federation admin, coach, referee, club chair, secretary – they get more or fewer rights. From tournament registration via app to entry-fee settlement.",
        },
        {
          title: "Websites / CMS",
          text: "A content management system for the national federation and for every club. Calendar, board and officials, squads and news are embedded automatically; every club gets its own homepage on a subdomain.",
        },
        {
          title: "Event management",
          text: "Create competitions, training sessions, events and gradings and show them automatically in the calendar on the website and in the app. Federation events are visible to everyone, club events on the respective club page.",
        },
        {
          title: "Licence management",
          text: "Annual membership fees, coaching licences or historical licences: complete licence management with an automatic list of everyone whose licence is about to expire.",
        },
        {
          title: "League system",
          text: "A comprehensive national and regional league system with results, tables and rankings. Results can be integrated into any other system or app through an open API.",
        },
        {
          title: "Qualification paths",
          text: "Define hierarchies of qualifications and generate a qualification path for every athlete automatically. After a passed grading – a kyu exam in judo, for instance – the new grade is recorded automatically and is visible in the app right away.",
        },
        {
          title: "Ticketing",
          text: "No expensive external ticketing systems: create admission tickets directly in JAMA and sell them online. Tickets show up directly in the membership card app.",
        },
        {
          title: "Membership card app",
          text: "The current sports passport on the smartphone – for iPhone and Android. News, tournament calendar, membership and licence cards; parents manage their children's passports. With a dedicated coach and club-chair mode.",
        },
        {
          title: "Web shop with Stripe",
          text: "Sell merchandise, entry fees, licences or tickets online: the web shop can be implemented with payments through Stripe – orders and payments flow straight into the administration.",
        },
        {
          title: "Communication",
          text: "Inform members in real time about new tournaments, rescheduled dates and measures – directly and just in time.",
        },
      ],
    },
    references: {
      title: ["JAMA", "in use"],
      items: [
        {
          sport: "Judo",
          title: "JudoJama",
          text: "Federation and club administration for judo – with the “JudoAustria” app on Google Play: tournament calendar, news and a digital membership card.",
          link: "judojama.com",
          url: "https://www.judojama.com/",
          appLink: "JudoAustria on Google Play",
          appUrl: "https://play.google.com/store/apps/details?id=org.judojama.judoaustria",
        },
      ],
    },
    cta: {
      title: "All from a single source.",
      text: "Website, administration, app, ticketing and web shop – tailored to your federation or club, whatever the sport. We are happy to show you JAMA live.",
      button: "Get in touch",
      secondary: "judojama.com",
    },
  },
};

// Other languages: translations of the English copy (same structure as en).
const landing: Record<Lang, LandingCopy> = {
  de,
  en,
  fr: frCopy,
  es: esCopy,
  it: itCopy,
  pl: plCopy,
  zh: zhCopy,
  ja: jaCopy,
  ko: koCopy,
};

export default landing;
